import { KubernetesObject, loadAllYaml, loadYaml } from "@kubernetes/client-node"
import { jsonPath, mergeObject, sleep } from "yam-operator/util"
import { K8SResourceType, OperateFunction, PlanContext } from "yam-types"
import { NativeYamlResource, PatchOperation } from "../types.d"

const yamlOperator: OperateFunction<NativeYamlResource> = async (plan, diff) => {
  if (diff.hasDeleted) {
    plan.log.warn(`These resources won't be deleted even removed from App Model, please delete them manually: ${diff.deletedItems.map(x => x.files?.join(',')).join(',')}`)
  }
  for (const resourceFiles of diff.currentItems) {
    await handleExtraFiles(plan, resourceFiles.files, resourceFiles.patch)
  }
}

export async function handleExtraFiles(plan: PlanContext, extraFiles?: string[], patch?: PatchOperation): Promise<void> {
  if (!extraFiles || extraFiles.length === 0) {
    return
  }
  for (const extraFile of extraFiles) {
    // CAN NOT support Directory because it needs to be specific files, it will be persist to plan file
    // MAKE declarative yaml files for better read experience and avoid security concerns
    const rawResource = await plan.renderTemplate(extraFile, false)
    try {
      const content = loadYaml(rawResource) as KubernetesObject
      const name = content.metadata?.name
      const kind = content.kind || ''
      plan.action(async (ctx) => {
        // If JSON Path Patch mode, handle in other way
        if (patch) {
          ctx.log.info(`patching resource with JSON path patching: '${JSON.stringify(patch)}', `)
          const result = await ctx.k8sClient.find({ kind: patch.kind, name: patch.name, namespace: patch.namespace || plan.data.namespace })
          if (result.length === 0) {
            ctx.log.error(`can not patch resource, resource object not found.`)
          }
          const patchTarget = result[0] as KubernetesObject
          const tmpObj = jsonPath(patch.jsonPath, patchTarget)
          if (!tmpObj || tmpObj.length === 0) {
            ctx.log.error(`can not patch resource, JSON path not found.`)
            return
          }
          for (const obj of tmpObj) {
            if (typeof obj !== "object" || obj instanceof Array) {
              ctx.log.error(`can not patch resource, only 'object' target could be patched.`)
              return
            } else {
              mergeObject(obj, content, patch.operator === "replace", patch.operator === "complement")
            }
          }
          await ctx.k8sClient.apply(patchTarget)
          return
        }

        // Check immutable resources
        if (kind === K8SResourceType.Pod || kind === K8SResourceType.Job) {
          const result = await ctx.k8sClient.find({ kind, name, namespace: content.metadata?.namespace })
          if (result.length > 0) {
            ctx.log.warn(`${kind} - ${name} already exists, immutable resources can not be applied twice, please delete it at first or modify it manually.`)
            return
          }
        }
        await ctx.k8sClient.apply(content)
        if (kind === K8SResourceType.CustomResourceDefinition) {
          // hack: wait 1 second for CRD taking effect
          await sleep(1000)
        }
      }, 'add-extra-file:' + extraFile)
    } catch (e) {
      const resources = loadAllYaml(rawResource) as KubernetesObject[]
      plan.action(async (ctx) => {
        for (const resource of resources) {
          await ctx.k8sClient.apply(resource)
        }
      }, 'add-extra-file:' + extraFile)
    }
  }
}

export default yamlOperator